import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import {
  HomeIcon,
  PluginIcon as DefaultPluginIcon,
  SoundActiveIcon,
  SoundInactiveIcon
} from "../../../ui/_assets/icons";
import {
  getBackgroundColor,
  getColor,
  getDisabledColor,
  getHoverColor
} from "../../../ui/_themes/helpers";
import { E2EIdentifier } from "../../e2e/E2EIdentifier";
import { formatTime } from "../Footer/Common/Footer.utils";
import {
  triggerHomeRequest,
  triggerPluginRequest
} from "../Header/Common/Header.actions";
import { triggerMuteChangeRequest } from "../Keypad/Common/Keypad.actions";

export interface ITopBarAction {
  onClickOnHome: () => void;
  onClickOnPlugin: () => void;
  onClickOnSound: (value: boolean) => void;
  homeAvailable: boolean;
  homeEnabled: boolean;
  pluginAvailable: boolean;
  pluginEnabled: boolean;
  soundEnable: boolean;
  soundMuted: boolean;
  clockAvailable: boolean;
  clockDate: Date;
  pluginMenuButtonIcon: string;
}

export const TopBarView = ({
  onClickOnHome,
  onClickOnPlugin,
  onClickOnSound,
  soundEnable,
  soundMuted,
  homeAvailable,
  homeEnabled,
  pluginAvailable,
  pluginMenuButtonIcon,
  pluginEnabled,
  clockAvailable,
  clockDate
}: ITopBarAction) => {
  const onSoundClick = () => onClickOnSound(!soundMuted);

  const PluginIcon = pluginMenuButtonIcon
    ? CustomPluginIcon
    : DefaultPluginIcon;

  return (
    <Wrapper id="playui-topbar">
      <IconsContainer>
        {homeAvailable && (
          <Home
            disabled={!homeEnabled}
            onClick={homeEnabled ? onClickOnHome : null}
            data-e2e-id={E2EIdentifier.TOPBAR_HOME_BUTTON}
          >
            <HomeIcon />
          </Home>
        )}
        {pluginAvailable && (
          <Plugin
            disabled={!pluginEnabled}
            onClick={pluginEnabled ? onClickOnPlugin : null}
            data-e2e-id={E2EIdentifier.TOPBAR_PLUGIN_BUTTON}
          >
            <PluginIcon icon={pluginMenuButtonIcon} />
          </Plugin>
        )}
        <Sound
          disabled={!soundEnable}
          onClick={soundEnable ? onSoundClick : null}
          data-e2e-id={E2EIdentifier.TOPBAR_SOUND_BUTTON}
        >
          {soundMuted ? <SoundInactiveIcon /> : <SoundActiveIcon />}
        </Sound>
      </IconsContainer>
      {clockAvailable && (
        <Clock data-e2e-id={E2EIdentifier.TOPBAR_CLOCK}>
          {formatTime(clockDate)}
        </Clock>
      )}
    </Wrapper>
  );
};

export const Clock = styled.div`
  color: ${getColor("topBarSystemClock", "topBar.systemClock")};
  font-size: 0.75em;
  align-items: center;
`;

export const Home = styled.div`
  color: ${getColor("topBarHomeIcon", "topBar.homeIcon")};

  &[disabled] {
    color: ${getDisabledColor("topBarHomeIcon", "topBar.homeIcon")};
  }
`;

export const Plugin = styled.div`
  color: ${getColor("topBarPluginMenuIcon", "topBar.pluginMenuIcon")};

  &[disabled] {
    color: ${getDisabledColor("topBarPluginMenuIcon", "topBar.pluginMenuIcon")};
  }
`;

export const Sound = styled.div`
  color: ${getColor("topBarSoundActiveIcon", "topBar.soundActiveIcon")};

  &[disabled] {
    color: ${getDisabledColor(
      "topBarSoundInactiveIcon",
      "topBar.soundInactiveIcon"
    )};
  }
`;

export const CustomPluginIcon = styled.div`
  background-image: url(${props => props.icon});
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  width: 1.6em;
  height: 1.6em;
`;

export const IconsContainer = styled.div`
  ${Home}, ${Plugin}, ${CustomPluginIcon}, ${Sound} {
    :not([disabled]) {
      cursor: pointer;
    }

    @media (hover: hover){
      &:hover:not([disabled]) {
        color: ${getHoverColor("button")};
      }
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background: ${getBackgroundColor("topBar", "topBar")};
  pointer-events: all;
  padding: 2.8em 1em 1em;
  width: 100%;

  @media (orientation: landscape) {
    padding: 0.6em 1em;
    font-size: 0.8em;
  }

  > div {
    display: flex;
  }

  ${Home},${Plugin},${Sound}{

    margin: 0 0.3em;

    &:first-child {
      margin-left: 0;
    }

    svg {
      width: 1.6em;
      height: 1.6em;
      background: ${getBackgroundColor("topBar", "topBar")};
    }
  }
`;

interface ITopBarBooleanValueAction {
  keypad: {
    muted: boolean;
    soundButtonDisabled: boolean;
  };
  header: {
    homeAvailable: boolean;
    homeEnabled: boolean;
    pluginAvailable: boolean;
    pluginEnabled: boolean;
    pluginMenuButtonIcon: string;
  };
  footer: {
    clockAvailable: boolean;
    clockDate: Date;
  };
}

const mapPropsToState = ({
  keypad,
  header,
  footer
}: ITopBarBooleanValueAction) => ({
  soundEnable: !keypad.soundButtonDisabled,
  soundMuted: keypad.muted,
  homeAvailable: header.homeAvailable,
  homeEnabled: header.homeEnabled,
  pluginAvailable: header.pluginAvailable,
  pluginEnabled: header.pluginEnabled,
  pluginMenuButtonIcon: header.pluginMenuButtonIcon,
  clockAvailable: footer.clockAvailable,
  clockDate: footer.clockDate
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onClickOnSound: (value: boolean) => dispatch(triggerMuteChangeRequest(value)),
  onClickOnHome: () => dispatch(triggerHomeRequest()),
  onClickOnPlugin: () => dispatch(triggerPluginRequest())
});

export const TopBarContainer: any = connect(
  mapPropsToState,
  mapDispatchToProps
)(TopBarView);
